import React from 'react';
import { Button, DatePicker, Space } from 'antd';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { setAppliedFilters } from 'store/slices/dataDocumentsSlice';
import { fetchAvailableDocuments } from 'store/reducers/documentsCreator';
import { DocumentFilters } from 'interfaces';
import { serializeFiltersToUrl } from 'helpers/documentFiltersUrl';
import classes from './DocumentTable.module.scss';

const { RangePicker } = DatePicker;

interface Props {
  confirm: () => void;
  clearFilters: () => void;
  close: () => void;
}

const DateRangeFilterDropdown = ({ confirm, clearFilters, close }: Props) => {
  const { appliedFilters, currentFolder } = useAppSelector(store => store.documents);
  const { dataRoom } = useAppSelector(store => store.dataRoom);
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [dates, setDates] = React.useState<[Dayjs | null, Dayjs | null] | null>(() => {
    if (appliedFilters.dateFrom || appliedFilters.dateTo) {
      return [
        appliedFilters.dateFrom ? dayjs(appliedFilters.dateFrom.substring(0, 10)) : null,
        appliedFilters.dateTo ? dayjs(appliedFilters.dateTo.substring(0, 10)) : null,
      ];
    }
    return null;
  });

  React.useEffect(() => {
    if (appliedFilters.dateFrom || appliedFilters.dateTo) {
      setDates([
        appliedFilters.dateFrom ? dayjs(appliedFilters.dateFrom.substring(0, 10)) : null,
        appliedFilters.dateTo ? dayjs(appliedFilters.dateTo.substring(0, 10)) : null,
      ]);
    } else {
      setDates(null);
    }
  }, [appliedFilters.dateFrom, appliedFilters.dateTo]);

  const rangePresets: { label: string; value: [Dayjs, Dayjs] }[] = [
    { label: t('Documents.filters.presets.7'), value: [dayjs().add(-7, 'd'), dayjs()] },
    { label: t('Documents.filters.presets.14'), value: [dayjs().add(-14, 'd'), dayjs()] },
    { label: t('Documents.filters.presets.30'), value: [dayjs().add(-30, 'd'), dayjs()] },
  ];

  const handleApply = () => {
    const nextFilters: DocumentFilters = {
      ...appliedFilters,
      dateFrom: dates?.[0] ? `${dates[0].format('YYYY-MM-DD')}T00:00:00.000Z` : null,
      dateTo: dates?.[1] ? `${dates[1].format('YYYY-MM-DD')}T23:59:59.999Z` : null,
    };
    dispatch(setAppliedFilters(nextFilters));
    setSearchParams(serializeFiltersToUrl(searchParams, nextFilters), { replace: true });
    dispatch(fetchAvailableDocuments({
      roomId: dataRoom!.id,
      folderId: currentFolder[0]?.id || null,
      filters: nextFilters,
    }));
    close();
  };

  const handleReset = () => {
    setDates(null);
    const nextFilters: DocumentFilters = { ...appliedFilters, dateFrom: null, dateTo: null };
    dispatch(setAppliedFilters(nextFilters));
    setSearchParams(serializeFiltersToUrl(searchParams, nextFilters), { replace: true });
    dispatch(fetchAvailableDocuments({
      roomId: dataRoom!.id,
      folderId: currentFolder[0]?.id || null,
      filters: nextFilters,
    }));
    close();
  };

  return (
    <div className={classes.dateFilterDropdown} onKeyDown={e => e.stopPropagation()}>
      <RangePicker
        format="DD.MM.YYYY"
        value={dates}
        presets={rangePresets}
        onChange={val => setDates(val as [Dayjs | null, Dayjs | null])}
        className={classes.dateRangePicker}
      />
      <Space>
        <Button type="primary" size="small" onClick={handleApply}>
          {t('Documents.filters.apply')}
        </Button>
        <Button size="small" onClick={handleReset}>
          {t('Documents.filters.reset')}
        </Button>
        <Button type="text" size="small" onClick={close}>
          {t('Documents.filters.close')}
        </Button>
      </Space>
    </div>
  );
};

export default DateRangeFilterDropdown;
